import styles from './IconCard.module.scss';

import type { Player } from '@lottiefiles/react-lottie-player';
import React from 'react';

import { ImageSourceType } from 'api/contentful/contentTypes.cloverdotcom';
import { AnimatedIcon } from 'components/AnimatedIcon/AnimatedIcon';
import { ContentImage } from 'components/ContentImage/ContentImage';
import { ContentLinkV2 } from 'components/ContentLinkV2/ContentLinkV2';
import { TextV2 } from 'components/TextV2/TextV2';
import { useBreakpoints } from 'hooks/useBreakpoints';
import { toEntry, AppType, WithCMS } from 'services/cms/typesUtil';
import { ICON_CARD_TEST_ID } from './IconCard.const';

type IconCardProps = AppType.Component<{
  item: WithCMS<AppType.Content>;
}>;

export const IconCard: React.FC<IconCardProps> = ({ item }) => {
  const { headline, body, desktopTabletImage, mobileImage, ctaLink } = item || {};

  const { browser } = useBreakpoints();
  const isMobile = browser.is.mobile;
  const isDesktopOrLarge = browser.is.desktop;
  const animationRef = React.createRef<Player>();

  const image = isMobile ? mobileImage : desktopTabletImage;

  const toggleAnimation = (play: boolean) => {
    if (animationRef.current && isDesktopOrLarge) {
      play ? animationRef.current?.play() : animationRef.current?.stop();
    }
  };

  const renderImage = () => (
    <div className={styles.cardImage}>
      {image.file?.contentType === ImageSourceType.JSON ? (
        <AnimatedIcon autoplay={false} src={image.file?.url} ref={animationRef} />
      ) : (
        <ContentImage entry={image} alt="" />
      )}
    </div>
  );

  const renderLink = () => (
    <ContentLinkV2 entry={toEntry(ctaLink)} hideTitle className={styles.cardCta}>
      <TextV2 value={headline} variant="h3-medium" tagName="h3" className={styles.cardHeadline} />
    </ContentLinkV2>
  );

  return (
    <div
      className={styles.cardContainer}
      onMouseEnter={() => toggleAnimation(true)}
      onMouseLeave={() => toggleAnimation(false)}
      data-testid={ICON_CARD_TEST_ID.card}
    >
      {image && renderImage()}
      {ctaLink && renderLink()}
      {body && <TextV2 value={body} variant="body" tagName="p" className={styles.cardDescription} />}
    </div>
  );
};
